import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { ChevronLeft, Maximize2, Volume2, VolumeX, X } from "lucide-react";

/**
 * NeonSerpent — MystiCast Arcade wrapper for the Neon Serpent game.
 *
 * Loads the standalone Vite client from /games/neon-serpent/ in an iframe and
 * bridges the identity/lifecycle handshake described in
 * integration/mysticast/postmessage-protocol.md:
 *   → MYSTICAST_INITIALIZE      (locale, embed mode)
 *   → MYSTICAST_SET_PROFILE     (username, display name, avatar)
 *   → MYSTICAST_SET_AUTH_TOKEN  (MystiCast JWT; server verifies HS256 sig)
 *   → MYSTICAST_SET_VOLUME
 *   → MYSTICAST_PAUSE / MYSTICAST_RESUME
 *   → MYSTICAST_REQUEST_EXIT
 *
 *   ← MYSTICAST_LOADED / MYSTICAST_READY / MYSTICAST_ERROR
 *   ← MYSTICAST_MATCH_JOINED / MYSTICAST_PLAYER_DEATH / MYSTICAST_PLAYER_COUNT
 *   ← MYSTICAST_EXIT
 *
 * The iframe origin is deliberately same-origin (the static bundle sits under
 * MystiCast's `/games/neon-serpent/` path) so that postMessage's origin check
 * matches window.location.origin without any cross-origin CSP work.
 */
export default function NeonSerpent() {
  const nav = useNavigate();
  const { user } = useAuth();
  const iframeRef = useRef(null);
  const [muted, setMuted] = useState(false);
  const [ready, setReady] = useState(false);
  const [status, setStatus] = useState("Loading…");
  const [playerCount, setPlayerCount] = useState(1);

  const origin = useMemo(() => window.location.origin, []);
  const src = "/embed/neon-serpent/index.html";

  // ---- Send a MystiCast → game message. Only fires when the iframe is ready.
  const send = (type, payload = null) => {
    const w = iframeRef.current?.contentWindow;
    if (!w) return;
    w.postMessage({ source: "mysticast", version: 1, type, payload }, origin);
  };

  // ---- Listen for game → MystiCast messages.
  useEffect(() => {
    const onMsg = (e) => {
      if (e.origin !== origin) return;
      const d = e.data;
      if (!d || d.source !== "neon-serpent" || d.version !== 1) return;
      switch (d.type) {
        case "MYSTICAST_LOADED":
          // The game has parsed and is ready to receive identity + auth.
          send("MYSTICAST_INITIALIZE", { locale: "en", embedded: true });
          if (user) {
            send("MYSTICAST_SET_PROFILE", {
              userId: user.id,
              username: user.username,
              displayName: user.username,
              avatarUrl: user.avatar_url || null,
            });
            const token = typeof window !== "undefined" ? localStorage.getItem("sc_token") : null;
            if (token) send("MYSTICAST_SET_AUTH_TOKEN", { token });
          }
          send("MYSTICAST_SET_VOLUME", { level: muted ? 0 : 0.8 });
          setStatus("Connecting to server…");
          break;
        case "MYSTICAST_READY":
          setReady(true);
          setStatus("Ready");
          break;
        case "MYSTICAST_MATCH_JOINED":
          setStatus(`In match · ${d.payload?.roomId || ""}`);
          break;
        case "MYSTICAST_PLAYER_COUNT":
          setPlayerCount(d.payload?.count || 1);
          break;
        case "MYSTICAST_PLAYER_DEATH":
          setStatus("You died. Respawning…");
          break;
        case "MYSTICAST_ERROR":
          setStatus(`Error: ${d.payload?.message || "unknown"}`);
          break;
        case "MYSTICAST_EXIT":
          nav("/games");
          break;
        default:
          break;
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [origin, user, muted, nav]);

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    send("MYSTICAST_SET_VOLUME", { level: next ? 0 : 0.8 });
  };

  const requestFullscreen = async () => {
    const el = iframeRef.current;
    if (!el) return;
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await el.requestFullscreen();
    } catch {
      /* browser refused */
    }
  };

  const exit = () => {
    send("MYSTICAST_REQUEST_EXIT");
    nav("/games");
  };

  return (
    <div className="fixed inset-0 bg-black text-white flex flex-col" style={{ zIndex: 9999 }} data-testid="neon-serpent-page">
      {/* Top chrome */}
      <div className="flex items-center gap-3 px-4 py-2 border-b border-white/10 bg-black/80 backdrop-blur">
        <button onClick={exit} className="p-2 rounded hover:bg-white/5" data-testid="ns-exit" aria-label="Exit game">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <div className="sc-mono text-[10px] tracking-widest" style={{ color: "var(--sc-cyan)" }}>◤ MYSTICAST ARCADE</div>
          <div className="text-sm font-semibold">Neon Serpent</div>
        </div>
        <div className="sc-mono text-[11px] text-zinc-400 hidden sm:block" data-testid="ns-status">
          {status} · {playerCount} online
        </div>
        <button onClick={toggleMute} className="p-2 rounded hover:bg-white/5" data-testid="ns-mute" aria-label={muted ? "Unmute" : "Mute"}>
          {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
        </button>
        <button onClick={requestFullscreen} className="p-2 rounded hover:bg-white/5" data-testid="ns-fullscreen" aria-label="Fullscreen">
          <Maximize2 className="w-5 h-5" />
        </button>
        <button onClick={exit} className="p-2 rounded hover:bg-white/5" data-testid="ns-close" aria-label="Close">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Game iframe */}
      <div className="relative flex-1">
        {!ready && (
          <div
            className="absolute inset-0 flex items-center justify-center text-zinc-400 sc-mono text-xs"
            style={{ pointerEvents: "none" }}
            data-testid="ns-loading-overlay"
          >
            ◤ {status}
          </div>
        )}
        <iframe
          ref={iframeRef}
          src={src}
          title="Neon Serpent"
          className="w-full h-full border-0 block relative"
          allow="fullscreen; autoplay; gamepad *"
          onLoad={() => { setReady(true); setStatus("Ready"); }}
          data-testid="ns-iframe"
        />
      </div>
    </div>
  );
}
